/**
 * tableExport — the SINGLE CSV + PDF export implementation for the External
 * module. Replaces the ~15 hand-written exportCsv() copies and the one-off
 * autotable export. Both DataTable-based and BESPOKE tables call these so every
 * table reaches export parity from one place.
 *
 *   exportCsv(rows, columns, { filename, title })
 *   exportPdf(rows, columns, { filename, title, subtitle, orientation })
 *
 * `rows` should already be in display order with filters applied (pass what the
 * table currently shows). `columns` are the shared column descriptors; the cell
 * value is resolved via formatCell so CSV/PDF match the on-screen text.
 */
import jsPDF from 'jspdf'
import { autoTable } from 'jspdf-autotable'
import { exportBlockedInDemoMode } from '@/utils/demoSpoof'
import { formatCell } from './tableFormat'
import { isNumericType } from './tableHelpers'

function csvEscape(s) {
  const v = s == null ? '' : String(s)
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

export function exportCsv(rows, columns, { filename = 'export.csv' } = {}) {
  if (exportBlockedInDemoMode()) return
  const cols = columns.filter(c => c.exportable !== false)
  const header = cols.map(c => csvEscape(c.header))
  const lines = [header.join(',')]
  for (const row of rows) {
    lines.push(cols.map(c => csvEscape(rawExportValue(c, row))).join(','))
  }
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, filename)
}

// For CSV we prefer the raw numeric value (no $ / % / commas) so the file is
// spreadsheet-friendly; text/date use the formatted display.
function rawExportValue(col, row) {
  const val = row[col.field]
  if (isNumericType(col.type)) {
    return val == null || val === '' || Number.isNaN(Number(val)) ? '' : Number(val)
  }
  if (val == null || val === '') return ''
  if (typeof col.format === 'function') return col.format(val, row)
  return formatCell(col, row)
}

export function exportPdf(rows, columns, {
  filename = 'export.pdf', title = 'Report', subtitle = '', orientation = 'l',
} = {}) {
  if (exportBlockedInDemoMode()) return
  const cols = columns.filter(c => c.exportable !== false)
  const doc = new jsPDF(orientation, 'pt', 'letter')
  const marginX = 40

  autoTable(doc, {
    startY: subtitle ? 66 : 56,
    margin: { top: subtitle ? 66 : 56, left: marginX, right: marginX, bottom: 40 },
    head: [cols.map(c => c.header)],
    body: rows.map(row => cols.map(c => formatCell(c, row))),
    styles: { font: 'Helvetica', fontSize: 7.5, textColor: [34, 31, 28], cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fontSize: 7, fontStyle: 'bold', fillColor: [250, 249, 246], textColor: [125, 119, 112], lineWidth: { bottom: 0.75 }, lineColor: [207, 200, 190] },
    columnStyles: cols.reduce((acc, c, i) => {
      acc[i] = { halign: c.align || (isNumericType(c.type) ? 'right' : 'left') }
      if (c.width) acc[i].cellWidth = c.width
      return acc
    }, {}),
    didDrawPage: () => {
      doc.setFont('Helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(34, 31, 28)
      doc.text(title, marginX, 30)
      if (subtitle) {
        doc.setFont('Helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(169, 162, 154)
        doc.text(subtitle, marginX, 44)
      }
    },
    rowPageBreak: 'avoid',
  })
  doc.save(filename)
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
